// src/components/admin/ShiftManagement.jsx
// v2 — all roles fetched via dedicated endpoint, no pagination issue
import React, { useState, useEffect, useCallback } from 'react';
import { shiftAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

// ── Role display helpers ──────────────────────────────────────────────────────
const ROLE_LABELS = {
  ADMIN:                  'Admin',
  PRINCIPAL:              'Principal',
  DEAN:                   'Dean',
  FACULTY:                'Faculty',
  FINANCE:                'Finance',
  HOSTEL_ADMIN:           'Hostel Admin',
  LIBRARY_STAFF:          'Library Staff',
  TRANSPORT_MANAGER:      'Transport Manager',
  recepsonist:            'Receptionist',
  LAB_TECH_MICROBIOLOGY:  'Lab Tech – Microbiology',
  LAB_TECH_PATHOLOGY:     'Lab Tech – Pathology',
  LAB_TECH_BIOCHEMISTRY:  'Lab Tech – Biochemistry',
  BILLING_MEMBER:         'Billing Member',
  BILLING_MANAGER:        'Billing Manager',
  SAMPLE_COLLECTOR:       'Sample Collector',
  REPORT_GENERATION:      'Report Generation',
  GRIEVANCE_OFFICER:      'Grievance Officer',
};

const ROLE_GROUPS = [
  { label: 'Academic',          color: '#2563eb', roles: ['ADMIN','PRINCIPAL','DEAN','FACULTY'] },
  { label: 'Lab & Clinical',    color: '#9333ea', roles: ['recepsonist','LAB_TECH_MICROBIOLOGY','LAB_TECH_PATHOLOGY','LAB_TECH_BIOCHEMISTRY','SAMPLE_COLLECTOR','REPORT_GENERATION'] },
  { label: 'Finance & Billing', color: '#16a34a', roles: ['FINANCE','BILLING_MEMBER','BILLING_MANAGER'] },
  { label: 'Support Services',  color: '#ea580c', roles: ['HOSTEL_ADMIN','LIBRARY_STAFF','TRANSPORT_MANAGER','GRIEVANCE_OFFICER'] },
];

const roleColor = (role) => (ROLE_GROUPS.find((g) => g.roles.includes(role))?.color) || '#64748b';

const SHIFT_PALETTE = ['#2563eb','#16a34a','#9333ea','#ea580c','#0891b2','#be185d','#65a30d','#b45309'];

const fmt    = (t) => (t ? t.slice(0, 5) : '');
const durOf  = (s, e) => {
  if (!s || !e) return '';
  const [sh,sm] = s.split(':').map(Number);
  const [eh,em] = e.split(':').map(Number);
  let m = (eh*60+em) - (sh*60+sm);
  if (m <= 0) m += 1440;
  return `${Math.floor(m/60)}h ${(m%60).toString().padStart(2,'0')}m`;
};

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDismiss, 3800);
    return () => clearTimeout(id);
  }, [toast, onDismiss]);

  if (!toast) return null;
  const ok = toast.type !== 'error';
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      background: ok ? '#dcfce7' : '#fee2e2',
      border:`1px solid ${ok?'#86efac':'#fca5a5'}`,
      color: ok ? '#166534' : '#991b1b',
      borderRadius:10, padding:'12px 18px', maxWidth:400,
      boxShadow:'0 8px 30px rgba(0,0,0,.14)',
      display:'flex', alignItems:'flex-start', gap:10,
      fontSize:'0.86rem', fontWeight:500,
      animation:'smSlide .25s ease',
    }}>
      <span style={{marginTop:1}}>{ok ? '✓' : '✕'}</span>
      <span style={{flex:1}}>{toast.message}</span>
      <button onClick={onDismiss} style={{background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:'1rem',padding:0}}>×</button>
    </div>
  );
};

// ── Shift create/edit modal ───────────────────────────────────────────────────
const ShiftModal = ({ shift, onClose, onSaved }) => {
  const isEdit = !!shift;
  const [form, setForm] = useState({
    name:       shift?.name ?? '',
    start_time: shift ? fmt(shift.start_time) : '',
    end_time:   shift ? fmt(shift.end_time)   : '',
  });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  const dur      = durOf(form.start_time, form.end_time);
  const overnight = form.start_time && form.end_time && form.end_time < form.start_time;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim())                return setError('Shift name is required.');
    if (!form.start_time)                 return setError('Start time is required.');
    if (!form.end_time)                   return setError('End time is required.');
    if (form.start_time === form.end_time) return setError('Start and end times cannot be identical.');

    setSaving(true);
    try {
      let res;
      if (isEdit) { res = await shiftAPI.updateShift(shift.id, form); onSaved(res.shift, 'updated'); }
      else        { res = await shiftAPI.createShift(form);           onSaved(res.shift, 'created'); }
      onClose();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.name?.[0] || d?.end_time?.[0] || d?.start_time?.[0] || d?.error || d?.detail || 'Failed to save shift.');
    } finally { setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <h3 style={{margin:0,fontSize:'1.05rem',fontWeight:700,color:'#0f172a'}}>
            {isEdit ? '✎ Edit Shift' : '＋ Create Shift'}
          </h3>
          <button onClick={onClose} style={S.close}>×</button>
        </div>

        <form onSubmit={submit}>
          <label style={S.lbl}>Shift Name <span style={{color:'#ef4444'}}>*</span></label>
          <input style={S.inp} type="text" maxLength={100} autoFocus
            placeholder="e.g. Morning, Night Duty, Afternoon"
            value={form.name} onChange={(e)=>setForm(p=>({...p,name:e.target.value}))}
          />

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginTop:14}}>
            <div>
              <label style={S.lbl}>Start Time <span style={{color:'#ef4444'}}>*</span></label>
              <input style={S.inp} type="time" value={form.start_time}
                onChange={(e)=>setForm(p=>({...p,start_time:e.target.value}))} />
            </div>
            <div>
              <label style={S.lbl}>End Time <span style={{color:'#ef4444'}}>*</span></label>
              <input style={S.inp} type="time" value={form.end_time}
                onChange={(e)=>setForm(p=>({...p,end_time:e.target.value}))} />
            </div>
          </div>

          {dur && (
            <div style={{marginTop:12,background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'8px 12px',fontSize:'0.81rem',color:'#0369a1',display:'flex',gap:6,alignItems:'center'}}>
              <span>⏱</span>
              <span>Duration: <strong>{dur}</strong>
                {overnight && <span style={{marginLeft:8,color:'#7c3aed',fontWeight:600}}>🌙 Overnight</span>}
              </span>
            </div>
          )}

          {error && (
            <div style={{marginTop:12,background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:8,padding:'8px 12px',color:'#991b1b',fontSize:'0.81rem'}}>
              {error}
            </div>
          )}

          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:20}}>
            <button type="button" onClick={onClose} style={S.sec} disabled={saving}>Cancel</button>
            <button type="submit" style={{...S.pri,opacity:saving?0.7:1}} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Shift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ShiftManagement = () => {
  const [shifts, setShifts]           = useState([]);
  const [users, setUsers]             = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loadingShifts, setLS]        = useState(true);
  const [loadingUsers, setLU]         = useState(true);
  const [selected, setSelected]       = useState(new Set());
  const [search, setSearch]           = useState('');
  const [roleFilter, setRoleFilter]   = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editShift, setEditShift]     = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [savingRow, setSavingRow]     = useState(null);
  const [bulkSid, setBulkSid]         = useState('');
  const [bulkBusy, setBulkBusy]       = useState(false);
  const [toast, setToast]             = useState(null);

  const notify = useCallback((message, type='success') => setToast({message,type}), []);

  // ── Fetch shifts ────────────────────────────────────────────────────────────
  const fetchShifts = useCallback(async () => {
    setLS(true);
    try {
      const d = await shiftAPI.getShifts();
      setShifts(Array.isArray(d) ? d : (d.results || []));
    } catch { notify('Failed to load shifts.', 'error'); }
    finally { setLS(false); }
  }, [notify]);

  // ── Fetch ALL non-student users via dedicated unpaginated endpoint ───────────
  const fetchUsers = useCallback(async () => {
    setLU(true);
    try {
      const d = await shiftAPI.getShiftUsers();   // GET /auth/shifts/users/
      setUsers(Array.isArray(d) ? d : (d.results || []));
    } catch { notify('Failed to load users.', 'error'); }
    finally { setLU(false); }
  }, [notify]);

  useEffect(() => { fetchShifts(); fetchUsers(); }, [fetchShifts, fetchUsers]);

  // ── Filter ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let r = [...users];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(u =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        (u.email||'').toLowerCase().includes(q) ||
        (u.employee_id||'').toLowerCase().includes(q)
      );
    }
    if (roleFilter)             r = r.filter(u => u.role === roleFilter);
    if (shiftFilter==='unassigned') r = r.filter(u => !u.shift_info);
    else if (shiftFilter)       r = r.filter(u => u.shift_info?.id === Number(shiftFilter));
    setFiltered(r);
    setSelected(p => { const vis=new Set(r.map(u=>u.id)); return new Set([...p].filter(id=>vis.has(id))); });
  }, [users, search, roleFilter, shiftFilter]);

  // ── Select helpers ──────────────────────────────────────────────────────────
  const allChecked = filtered.length > 0 && filtered.every(u => selected.has(u.id));
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(filtered.map(u=>u.id)));
  const toggleOne  = id => setSelected(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });

  // ── Inline assign ───────────────────────────────────────────────────────────
  const inlineAssign = async (userId, shiftId) => {
    setSavingRow(userId);
    try {
      await shiftAPI.assignShift(userId, shiftId || null);
      const sh = shifts.find(s => s.id === Number(shiftId));
      setUsers(p => p.map(u => u.id!==userId ? u : {
        ...u,
        shift_info: sh ? {id:sh.id,name:sh.name,start_time:fmt(sh.start_time),end_time:fmt(sh.end_time)} : null,
      }));
      notify(shiftId ? 'Shift assigned.' : 'Shift removed.');
    } catch (err) { notify(err.response?.data?.error || 'Failed.', 'error'); }
    finally { setSavingRow(null); }
  };

  // ── Bulk assign ─────────────────────────────────────────────────────────────
  const applyBulk = async () => {
    if (!selected.size) return notify('Select at least one user.','error');
    const sid   = bulkSid ? Number(bulkSid) : null;
    const sName = shifts.find(s=>s.id===sid)?.name || null;
    if (!await confirmDialog(`${sid?`Assign "${sName}"`:'Remove shift'} for ${selected.size} user(s)?`)) return;
    setBulkBusy(true);
    try {
      const res = await shiftAPI.bulkAssignShift([...selected], sid);
      const sh  = shifts.find(s=>s.id===sid);
      setUsers(p => p.map(u => !selected.has(u.id) ? u : {
        ...u,
        shift_info: sh ? {id:sh.id,name:sh.name,start_time:fmt(sh.start_time),end_time:fmt(sh.end_time)} : null,
      }));
      setSelected(new Set()); setBulkSid('');
      notify(res.message || `Updated ${res.updated_count} user(s).`);
    } catch (err) { notify(err.response?.data?.error || 'Bulk assign failed.','error'); }
    finally { setBulkBusy(false); }
  };

  // ── Shift CRUD ──────────────────────────────────────────────────────────────
  const onShiftSaved = (saved, action) => {
    setShifts(p => action==='created' ? [...p,saved] : p.map(s=>s.id===saved.id?saved:s));
    notify(`Shift "${saved.name}" ${action}.`);
  };

  const deleteShift = async (sh) => {
    if (!await confirmDialog(`Delete "${sh.name}"?${sh.user_count>0?`\n\n⚠ ${sh.user_count} user(s) assigned — re-assign first.`:''}`)) return;
    setDeletingId(sh.id);
    try {
      await shiftAPI.deleteShift(sh.id);
      setShifts(p => p.filter(s=>s.id!==sh.id));
      setUsers(p => p.map(u => u.shift_info?.id===sh.id ? {...u,shift_info:null} : u));
      if (shiftFilter===String(sh.id)) setShiftFilter('');
      notify(`Shift "${sh.name}" deleted.`);
    } catch (err) { notify(err.response?.data?.error || 'Delete failed.','error'); }
    finally { setDeletingId(null); }
  };

  // ── Derived stats ───────────────────────────────────────────────────────────
  const unassigned    = users.filter(u=>!u.shift_info).length;
  const presentRoles  = [...new Set(users.map(u=>u.role))];

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:'inherit'}}>
      <Toast toast={toast} onDismiss={()=>setToast(null)} />

      {/* Stats row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:20}}>
        {[
          {label:'Total Staff',    val:users.length,                  c:'#2563eb',bg:'#eff6ff'},
          {label:'Shifts Defined', val:shifts.length,                 c:'#9333ea',bg:'#f5f3ff'},
          {label:'Assigned',       val:users.length - unassigned,     c:'#16a34a',bg:'#f0fdf4'},
          {label:'Unassigned',     val:unassigned,                    c:'#ea580c',bg:'#fff7ed'},
        ].map(s=>(
          <div key={s.label} style={{background:s.bg,border:`1px solid ${s.c}22`,borderRadius:12,padding:'14px 18px'}}>
            <div style={{fontSize:'1.6rem',fontWeight:800,color:s.c}}>{s.val}</div>
            <div style={{fontSize:'0.77rem',color:'#64748b',marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Shift cards */}
      <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:20,marginBottom:20}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div>
            <h3 style={{margin:0,fontSize:'0.97rem',fontWeight:700,color:'#0f172a'}}>Shift Definitions</h3>
            <p style={{margin:'3px 0 0',fontSize:'0.77rem',color:'#64748b'}}>
              {loadingShifts ? 'Loading…' : `${shifts.length} shift${shifts.length!==1?'s':''} — click a card to filter the table`}
            </p>
          </div>
          <button onClick={()=>{setEditShift(null);setShowModal(true);}} style={S.pri}>＋ New Shift</button>
        </div>

        {loadingShifts ? <LoadRow text="Loading shifts…" /> :
         shifts.length===0 ? <Empty icon="🕐" title="No shifts yet" sub='Click "New Shift" to create one.' /> : (
          <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
            {shifts.map((sh,idx)=>{
              const c      = SHIFT_PALETTE[idx%SHIFT_PALETTE.length];
              const active = shiftFilter===String(sh.id);
              const del    = deletingId===sh.id;
              return (
                <div key={sh.id} onClick={()=>setShiftFilter(active?'':String(sh.id))} style={{
                  border:`2px solid ${active?c:'#e2e8f0'}`,
                  background:active?`${c}0e`:'#fff',
                  borderRadius:12,padding:'12px 14px',cursor:'pointer',
                  transition:'all .15s',minWidth:178,maxWidth:218,position:'relative',
                }}>
                  <div style={{position:'absolute',top:8,right:8,display:'flex',gap:4}}>
                    <button onClick={e=>{e.stopPropagation();setEditShift(sh);setShowModal(true);}}
                      style={S.ibtn('#dbeafe','#1d4ed8')} title="Edit">✎</button>
                    <button onClick={e=>{e.stopPropagation();deleteShift(sh);}}
                      style={S.ibtn('#fee2e2','#b91c1c')} title="Delete" disabled={del}>
                      {del?<Spin size={10}/>:'✕'}
                    </button>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:8}}>
                    <span style={{width:10,height:10,borderRadius:'50%',background:c,flexShrink:0}}/>
                    <span style={{fontWeight:700,fontSize:'0.87rem',color:'#0f172a',paddingRight:42}}>
                      {sh.name}
                    </span>
                  </div>
                  <div style={{fontSize:'0.78rem',color:'#64748b'}}>
                    {fmt(sh.start_time)} – {fmt(sh.end_time)}
                    {sh.is_overnight && <span style={{marginLeft:5,color:'#7c3aed'}}>🌙</span>}
                  </div>
                  <div style={{fontSize:'0.73rem',color:'#94a3b8',marginTop:2}}>
                    {sh.duration_display || durOf(sh.start_time,sh.end_time)}
                  </div>
                  <div style={{marginTop:10,display:'flex',alignItems:'center',gap:8}}>
                    <span style={{background:`${c}18`,color:c,borderRadius:99,padding:'2px 8px',fontSize:'0.73rem',fontWeight:600}}>
                      {sh.user_count} user{sh.user_count!==1?'s':''}
                    </span>
                    {active && <span style={{fontSize:'0.69rem',color:c,fontWeight:700}}>● filtering</span>}
                  </div>
                </div>
              );
            })}

            {/* Unassigned chip */}
            {(()=>{
              const active = shiftFilter==='unassigned';
              return (
                <div onClick={()=>setShiftFilter(active?'':'unassigned')} style={{
                  border:`2px solid ${active?'#94a3b8':'#e2e8f0'}`,
                  background:active?'#f8fafc':'#fff',
                  borderRadius:12,padding:'12px 14px',cursor:'pointer',transition:'all .15s',minWidth:140,
                }}>
                  <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:8}}>
                    <span style={{width:10,height:10,borderRadius:'50%',background:'#cbd5e1'}}/>
                    <span style={{fontWeight:700,fontSize:'0.87rem',color:'#475569'}}>Unassigned</span>
                  </div>
                  <div style={{fontSize:'0.73rem',color:'#94a3b8'}}>No shift assigned</div>
                  <div style={{marginTop:10}}>
                    <span style={{background:'#f1f5f9',color:'#64748b',borderRadius:99,padding:'2px 8px',fontSize:'0.73rem',fontWeight:600}}>
                      {unassigned} user{unassigned!==1?'s':''}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* User table */}
      <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'hidden'}}>

        {/* Toolbar */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
          <div>
            <h3 style={{margin:0,fontSize:'0.95rem',fontWeight:700,color:'#0f172a'}}>All Staff &amp; Faculty</h3>
            <p style={{margin:'2px 0 0',fontSize:'0.77rem',color:'#64748b'}}>
              {loadingUsers ? 'Loading…' : `Showing ${filtered.length} of ${users.length} users`}
            </p>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <input type="search" placeholder="Search name, email, ID…" value={search}
              onChange={e=>setSearch(e.target.value)} style={{...S.sel,width:200,padding:'7px 12px'}} />

            <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={S.sel}>
              <option value="">All Roles</option>
              {ROLE_GROUPS.map(grp=>{
                const present = grp.roles.filter(r=>presentRoles.includes(r));
                if (!present.length) return null;
                return (
                  <optgroup key={grp.label} label={grp.label}>
                    {present.map(r=><option key={r} value={r}>{ROLE_LABELS[r]||r}</option>)}
                  </optgroup>
                );
              })}
            </select>

            {(search||roleFilter||shiftFilter) && (
              <button onClick={()=>{setSearch('');setRoleFilter('');setShiftFilter('');}} style={{...S.sec,padding:'6px 12px'}}>
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {/* Bulk bar */}
        {selected.size>0 && (
          <div style={{padding:'10px 20px',background:'linear-gradient(90deg,#eff6ff,#f0fdf4)',borderBottom:'1px solid #bfdbfe',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:'0.84rem',color:'#1d4ed8'}}>
              {selected.size} user{selected.size!==1?'s':''} selected
            </span>
            <select value={bulkSid} onChange={e=>setBulkSid(e.target.value)}
              style={{...S.sel,borderColor:'#93c5fd'}}>
              <option value="">— Remove Shift —</option>
              {shifts.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={applyBulk} disabled={bulkBusy}
              style={{...S.pri,opacity:bulkBusy?0.7:1,display:'inline-flex',alignItems:'center',gap:6}}>
              {bulkBusy?<><Spin size={14}/> Applying…</>:'Apply to Selected'}
            </button>
            <button onClick={()=>setSelected(new Set())} style={{...S.sec,padding:'6px 12px'}}>Clear</button>
          </div>
        )}

        {/* Table */}
        {loadingUsers ? <LoadRow text="Loading users…" pad={44}/> :
         filtered.length===0 ? <Empty icon="🔍" title="No users match your filters."/> : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.83rem'}}>
              <thead>
                <tr style={{background:'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
                  <th style={S.th}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll}
                      style={{width:15,height:15,accentColor:'#2563eb',cursor:'pointer'}}/>
                  </th>
                  <th style={{...S.th,textAlign:'left'}}>Employee ID</th>
                  <th style={{...S.th,textAlign:'left'}}>Name / Email</th>
                  <th style={{...S.th,textAlign:'left'}}>Role</th>
                  <th style={{...S.th,textAlign:'left'}}>Current Shift</th>
                  <th style={{...S.th,textAlign:'left'}}>Assign Shift</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u=>{
                  const sel     = selected.has(u.id);
                  const busy    = savingRow===u.id;
                  const shIdx   = shifts.findIndex(s=>s.id===u.shift_info?.id);
                  const shColor = shIdx>=0 ? SHIFT_PALETTE[shIdx%SHIFT_PALETTE.length] : null;
                  const rColor  = roleColor(u.role);

                  return (
                    <tr key={u.id} style={{borderBottom:'1px solid #f8fafc',background:sel?'#eff6ff':'#fff',transition:'background .1s'}}>

                      {/* Checkbox */}
                      <td style={{...S.td,textAlign:'center'}}>
                        <input type="checkbox" checked={sel} onChange={()=>toggleOne(u.id)}
                          style={{width:15,height:15,accentColor:'#2563eb',cursor:'pointer'}}/>
                      </td>

                      {/* Employee ID */}
                      <td style={S.td}>
                        <span style={{fontFamily:'monospace',fontSize:'0.78rem',background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:5,padding:'2px 7px',color:'#334155',whiteSpace:'nowrap'}}>
                          {u.employee_id || '—'}
                        </span>
                      </td>

                      {/* Name */}
                      <td style={S.td}>
                        <div style={{fontWeight:600,color:'#0f172a'}}>
                          {(u.first_name||u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : u.username}
                        </div>
                        <div style={{fontSize:'0.73rem',color:'#94a3b8',marginTop:1}}>{u.email}</div>
                      </td>

                      {/* Role */}
                      <td style={S.td}>
                        <span style={{background:`${rColor}15`,color:rColor,borderRadius:6,padding:'3px 9px',fontSize:'0.74rem',fontWeight:700,whiteSpace:'nowrap'}}>
                          {ROLE_LABELS[u.role]||u.role}
                        </span>
                      </td>

                      {/* Current shift */}
                      <td style={S.td}>
                        {u.shift_info ? (
                          <div style={{display:'flex',alignItems:'center',gap:7}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:shColor||'#94a3b8',flexShrink:0}}/>
                            <div>
                              <div style={{fontWeight:600,fontSize:'0.82rem',color:'#0f172a'}}>{u.shift_info.name}</div>
                              <div style={{fontSize:'0.72rem',color:'#94a3b8'}}>{u.shift_info.start_time} – {u.shift_info.end_time}</div>
                            </div>
                          </div>
                        ) : (
                          <span style={{color:'#cbd5e1',fontStyle:'italic',fontSize:'0.8rem'}}>Not assigned</span>
                        )}
                      </td>

                      {/* Inline assign dropdown */}
                      <td style={S.td}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <select value={u.shift_info?.id??''} onChange={e=>inlineAssign(u.id,e.target.value)}
                            disabled={busy} style={{...S.sel,minWidth:168,opacity:busy?0.6:1}}>
                            <option value="">— None / Remove —</option>
                            {shifts.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          {busy && <Spin size={16}/>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <ShiftModal
          shift={editShift}
          onClose={()=>{setShowModal(false);setEditShift(null);}}
          onSaved={onShiftSaved}
        />
      )}

      <style>{`
        @keyframes smSpin  { to { transform:rotate(360deg); } }
        @keyframes smSlide { from{transform:translateY(14px);opacity:0} to{transform:translateY(0);opacity:1} }
      `}</style>
    </div>
  );
};

// ── Micro helpers ─────────────────────────────────────────────────────────────
const Spin = ({ size=20 }) => (
  <div style={{width:size,height:size,flexShrink:0,border:`${size>16?3:2}px solid #e2e8f0`,borderTopColor:'#2563eb',borderRadius:'50%',animation:'smSpin .7s linear infinite',display:'inline-block'}}/>
);
const LoadRow = ({ text, pad=24 }) => (
  <div style={{display:'flex',alignItems:'center',gap:12,color:'#64748b',fontSize:'0.85rem',padding:pad}}>
    <Spin/><span>{text}</span>
  </div>
);
const Empty = ({ icon, title, sub }) => (
  <div style={{textAlign:'center',padding:'38px 20px',color:'#0f172a',border:'2px dashed #e2e8f0',borderRadius:12,background:'#fafafa',margin:16}}>
    <div style={{fontSize:'2rem',marginBottom:8}}>{icon}</div>
    <strong>{title}</strong>
    {sub && <p style={{margin:'4px 0 0',color:'#64748b',fontSize:'0.82rem'}}>{sub}</p>}
  </div>
);

// ── Style tokens (object form so JSX can spread them) ─────────────────────────
const S = {
  overlay: {position:'fixed',inset:0,zIndex:1000,background:'rgba(15,23,42,.45)',display:'flex',alignItems:'center',justifyContent:'center',padding:16},
  modal:   {background:'#fff',borderRadius:14,padding:28,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,.2)'},
  close:   {background:'none',border:'none',fontSize:'1.4rem',cursor:'pointer',color:'#94a3b8',lineHeight:1,padding:'0 4px'},
  lbl:     {display:'block',fontSize:'0.79rem',fontWeight:600,color:'#374151',marginBottom:5,marginTop:14},
  inp:     {width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'8px 12px',fontSize:'0.86rem',outline:'none',color:'#0f172a',boxSizing:'border-box',fontFamily:'inherit'},
  pri:     {background:'#2563eb',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',fontSize:'0.85rem',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'},
  sec:     {background:'#f1f5f9',color:'#475569',border:'1px solid #e2e8f0',borderRadius:8,padding:'8px 16px',fontSize:'0.83rem',fontWeight:500,cursor:'pointer',whiteSpace:'nowrap'},
  sel:     {border:'1px solid #e2e8f0',borderRadius:8,padding:'7px 10px',fontSize:'0.82rem',outline:'none',background:'#fff',color:'#0f172a',cursor:'pointer'},
  th:      {padding:'10px 14px',fontSize:'0.71rem',fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.04em'},
  td:      {padding:'11px 14px',verticalAlign:'middle'},
  ibtn:    (bg,c)=>({background:bg,color:c,border:'none',borderRadius:6,width:24,height:24,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'0.78rem',fontWeight:700}),
};

export default ShiftManagement;